// AudiobookTab.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { Button, Card, Empty, Tag, Space, Typography, Tooltip, message, Slider, List, Progress, Row, Col, Modal, Select, Alert, Switch, Statistic, Input } from 'antd';
import type { TourProps } from 'antd';
import { SoundOutlined, PlusOutlined, PlayCircleOutlined, PauseCircleOutlined, LoadingOutlined, CheckCircleOutlined, ClockCircleOutlined, InfoCircleOutlined,
  AudioOutlined, DownloadOutlined, DeleteOutlined, RedoOutlined, UpOutlined, DownOutlined, CloudUploadOutlined, YoutubeOutlined, FileTextOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import AudiobookCreationModal from './AudiobookCreation';
import ExportAudiobookModal from './ExportAudiobookModal';
import { useTranslationStatus } from './TranslationStatusContext';
import AudioDownloadTag from './AudioDownloadTag';
import FeatherSpinner from './components/FeatherSpinner';
import LoadingCard from './components/LoadingCard';
import { TranslationStatus, CreateAudiobookParams, ChapterWordCounts } from './types';
import type { CreditTopUpContext } from './types/creditTopUp';
import { estimateUSD, formatUSD, shouldShowDollarEquivalents } from './utils/costUtils';
import { voiceNamesByLanguage, getVoiceDisplayName, VoiceMappings, LanguageVoiceMappings } from './voiceConfig';
import { countWordsIncludingMarkup } from './utils/wordcount';
import { useTourFlags } from './tours/useTourFlags';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import './AudiobookTab.css';
import { getTargetLanguageFromPair } from './utils/languagePairs';
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider';


const { Title, Text } = Typography;

const API_BASE = resolveFrontendApiBase();
const getSelectedTargetLanguage = (value: string): string => getTargetLanguageFromPair(value) || '';

const hasFullAudioFormat = (
  formats: Record<string, unknown> | undefined | null,
  selectedFormat: string,
): boolean => {
  if (!formats) return false;
  const normalized = selectedFormat.toLowerCase();
  const acceptedKeys = new Set([
    `full_audio_${normalized}`,
    `full_${normalized}`,
    normalized,
  ]);
  return Object.keys(formats).some(key => acceptedKeys.has(key.toLowerCase()));
};

// Define types for props and data structures
interface Chapter {
  chapter_guid: string;
  chapter_name: string;
  content?: string;
}

interface Book {
  title: string;
  author: string;
  chapters: Chapter[];
  coverUrl?: string;
}

interface Auth {
  currentUser: {
    uid: string;
  };
}

export interface ChapterAudioStatus {
  summary: {
    all_audio: number;
    partial_audio: number;
    no_audio: number;
    total_chapters: number;
  };
  intro: {
    status: string;
    has_audio: boolean;
    combined_audio_url?: string;
    voice?: string;
    aiModel?: string;  // Add this line
    processed_text?: string;
    audio_url?: string;
    formats?: { [format: string]: string };
  };
  outro: {
    status: string;
    has_audio: boolean;
    combined_audio_url?: string;
    voice?: string;
    aiModel?: string;  // Add this line
    processed_text?: string;
    audio_url?: string;
    formats?: { [format: string]: string };
  };
  full_book: {
    formats: {
      [format: string]: {
        url: string;
        timestamp?: string;
      };
    };
    full_audio_duration_seconds?: number;
    full_audio_human_readable?: string;	
  };
  chapters: {
    [chapterId: string]: {
      name: string;
      status: string;
      has_audio: boolean;
      combined_audio_url?: string;
      successful_chunks: number;
      total_chunks: number;
      voice?: string;
      aiModel?: string;  // Add this line
      formats?: { [format: string]: string };
    };
  };
  lpf_file?: string;
  lpf_timestamp?: string;
  lpf_generation_error?: string;
  youtube_video?: {
    url?: string;
    status?: string;
    error?: string;
  };  
}

interface YoutubeVideoGenerationModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onGenerate: () => void;
  creditBalance: number;
  totalWordCount: number;
  // Make this optional so parent isn’t forced to pass it
  fullAudioDurationSeconds?: number;
  bookTitle: string;
  bookCoverUrl?: string;
  selectedResolution: '480p' | '720p';
  onResolutionChange: (value: '480p' | '720p') => void;
  useCustomCover: boolean;
  onUseCustomCoverChange: (value: boolean) => void;
  customCoverPreview: string | null;
  onCustomCoverUpload: (file: File) => void;
  planId?: string;
  onTopUpCredits?: (context: CreditTopUpContext) => void;
}

export interface CreditBalance {
  credit_balance: number;
  planID?: string;
  temp_Credits?: number;
  forever_credits?: number;
  // New optional fields from backend for pending/available support
  pending_credits?: number;
  available_balance?: number;
}

interface AudiobookCreationOptions {
  version: string;
  voice: string;
  selectedChapters: string[];
  introText: string;
  outroText: string;
}

export type AudiobookTabTestOverrides = {
  initialChapterAudioStatus?: ChapterAudioStatus | null;
  initialCreditBalance?: CreditBalance | null;
  skipChapterStatusFetch?: boolean;
  skipCreditFetch?: boolean;
  youtubeGenerationImpl?: typeof runYoutubeVideoGeneration;
  youtubeDeletionImpl?: typeof runYoutubeVideoDeletion;
};

interface AudiobookTabProps {
  bookId: string;
  book: Book;
  selectedLanguage?: string;
  audiobookVersionOptions?: Array<{ value: string; label: string }>;
  onTranslateText?: (text: string, targetLang: string) => Promise<string>;
  auth: Auth;
  bookLanguage?: string;
  onLoadingChange?: (loading: boolean) => void;
  isLangTesterUser: boolean;
  chapterWordCounts: ChapterWordCounts;
  isBetaUser: boolean;
  languageCovers?: { [language: string]: string };
  startTourSignal?: number;
  onTopUpCredits?: (context: CreditTopUpContext) => void;
  testOverrides?: AudiobookTabTestOverrides;
}

interface ElevenlabsVoice extends Record<string, any> {
  preview_url: string;
}

type MessageApi = Pick<typeof message, 'success' | 'error'>;

export type YoutubeGenerationDeps = {
  authUser: { uid: string } | null;
  bookId: string;
  selectedLanguage: string;
  selectedResolution: '480p' | '720p';
  useCustomCover: boolean;
  customCoverFile: File | null;
  getTotalWordCount: () => number;
  creditBalance: CreditBalance | null;
  axiosInstance: typeof axios;
  messageApi: MessageApi;
  addOptimisticTranslation: (payload: TranslationStatus) => void;
  setYoutubeVideoUrl: (url: string | null) => void;
  setIsGeneratingYoutubeVideo: (value: boolean) => void;
  setIsYoutubeModalVisible: (value: boolean) => void;
};

export const runYoutubeVideoGeneration = async ({
  authUser,
  bookId,
  selectedLanguage,
  selectedResolution,
  useCustomCover,
  customCoverFile,
  getTotalWordCount,
  creditBalance,
  axiosInstance,
  messageApi,
  addOptimisticTranslation,
  setYoutubeVideoUrl,
  setIsGeneratingYoutubeVideo,
  setIsYoutubeModalVisible,
}: YoutubeGenerationDeps) => {
  if (!authUser) {
    messageApi.error('Not signed in');
    return;
  }

  setIsGeneratingYoutubeVideo(true);
  setIsYoutubeModalVisible(false);

  try {
    const totalWords = getTotalWordCount();
    const estimatedHours = totalWords / 13700;
    const generationTimeMinutes = Math.max(1, Math.ceil(estimatedHours));
    const creditsPerHour = selectedResolution === '720p' ? 1500 : 750;
    const creditsRequired = Math.ceil(estimatedHours * creditsPerHour);
    const currentCredits =
      (creditBalance?.available_balance ?? creditBalance?.credit_balance) ?? 0;

    if (currentCredits < creditsRequired) {
      messageApi.error(`Insufficient credits. You need ${creditsRequired} credits for this video.`);
      return;
    }

    const formData = new FormData();
    formData.append('userId', authUser.uid);
    formData.append('version', selectedLanguage);
    formData.append('resolution', selectedResolution);
    formData.append('useCustomCover', useCustomCover.toString());

    if (useCustomCover && customCoverFile) {
      formData.append('cover_image', customCoverFile);
    }

    const response = await axiosInstance.post(
      `${API_BASE}/generate-youtube-video/${bookId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    if (response.data.success) {
      if (response.data.video_url) {
        setYoutubeVideoUrl(response.data.video_url);
        messageApi.success('YouTube video already exists!');
      } else {
        messageApi.success('YouTube video generation started. This may take a while.');
        addOptimisticTranslation({
          lockGuid: `YoutubeVideo-${bookId}-${Date.now()}`,
          chapterGuid: 'youtube-video',
          chapterName: `YouTube Video Generation (${selectedResolution})`,
          sourceLang: selectedLanguage,
          targetLang: selectedLanguage,
          timestamp: new Date().toISOString(),
          status: 'pending',
          bookId,
          userId: authUser.uid,
          word_count: totalWords,
          estimatedTimeMinutes: generationTimeMinutes,
          error: '',
          type: 'AudioBookGeneration',
          version: selectedLanguage,
          clientIntent: true,
        });
      }
      return;
    }

    messageApi.error(response.data.error || 'Failed to generate YouTube video');
  } catch (error) {
    console.error('Error generating YouTube video:', error);
    messageApi.error('Failed to generate YouTube video. Please try again later.');
  } finally {
    setIsGeneratingYoutubeVideo(false);
  }
};

export type YoutubeDeletionDeps = {
  axiosInstance: typeof axios;
  messageApi: MessageApi;
  bookId: string;
  selectedLanguage: string;
  refreshYoutubeStatus: () => void;
  setYoutubeVideoUrl: (url: string | null) => void;
  setIsDeletingYoutubeVideo: (value: boolean) => void;
};

export const runYoutubeVideoDeletion = async ({
  axiosInstance,
  messageApi,
  bookId,
  selectedLanguage,
  refreshYoutubeStatus,
  setYoutubeVideoUrl,
  setIsDeletingYoutubeVideo,
}: YoutubeDeletionDeps) => {
  try {
    setIsDeletingYoutubeVideo(true);
    const response = await axiosInstance.delete(
      `${API_BASE}/delete-youtube-video/${bookId}`,
      { params: { version: selectedLanguage } }
    );

    if (response.data.success) {
      messageApi.success('YouTube video deleted successfully');
      setYoutubeVideoUrl(null);
      refreshYoutubeStatus();
      return;
    }

    messageApi.error(response.data.error || 'Failed to delete YouTube video');
  } catch (error) {
    console.error('Error deleting YouTube video:', error);
    messageApi.error('Failed to delete YouTube video. Please try again later.');
  } finally {
    setIsDeletingYoutubeVideo(false);
  }
};

const YoutubeVideoGenerationModal: React.FC<YoutubeVideoGenerationModalProps> = ({
  isVisible,
  onCancel,
  onGenerate,
  creditBalance,
  totalWordCount,
  fullAudioDurationSeconds,   
  bookTitle,
  bookCoverUrl,
  selectedResolution,
  onResolutionChange,
  useCustomCover,
  onUseCustomCoverChange,
  customCoverPreview,
  onCustomCoverUpload,
  planId,
  onTopUpCredits,
}) => {
	
  const estimatedHours = fullAudioDurationSeconds != null
      ? fullAudioDurationSeconds / 3600  // Direct conversion to hours
      : totalWordCount / 13700;
  	
  const creditsPerHour = selectedResolution === '720p' ? 1500 : 750;
  const creditsRequired = Math.ceil(estimatedHours * creditsPerHour);
  const hasEnoughCredits = creditBalance >= creditsRequired;
  const shortfallCredits = Math.max(creditsRequired - creditBalance, 0);
  const estimatedDuration = Math.ceil(estimatedHours * 60); // in minutes
  const generationTimeMinutes = Math.max(1, Math.ceil(estimatedHours))
  const [approxUsd, setApproxUsd] = React.useState<string | null>(null);
  const handleTopUpCredits = React.useCallback(() => {
    if (!onTopUpCredits) return;
    onTopUpCredits({
      source: 'audiobook_youtube_video',
      requiredCredits: creditsRequired,
      availableCredits: Math.max(0, Math.floor(creditBalance)),
      shortfallCredits,
    });
  }, [creditBalance, creditsRequired, onTopUpCredits, shortfallCredits]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!shouldShowDollarEquivalents()) { setApproxUsd(null); return; }
        if (!creditsRequired || creditsRequired <= 0) { setApproxUsd(null); return; }
        const usd = await estimateUSD(creditsRequired, planId);
        if (!cancelled) setApproxUsd(formatUSD(usd));
      } catch { if (!cancelled) setApproxUsd(null); }
    };
    run();
    return () => { cancelled = true; };
  }, [creditsRequired, planId, isVisible, selectedResolution]);

  return (
    <Modal
      className="audiobook-youtube-modal"
      title={<div className="audiobook-youtube-modal-title">Generate YouTube Video</div>}
      open={isVisible}
      onCancel={onCancel}
      footer={
        <div className="audiobook-youtube-modal-footer">
          <div className="audiobook-youtube-readiness" aria-live="polite">
            <Text strong>{hasEnoughCredits ? 'Ready to generate' : 'Add credits to continue'}</Text>
            <Text type="secondary">
              {creditsRequired.toLocaleString()} credits required for {selectedResolution}
              {approxUsd && (
                <Tooltip title="Approximate $ based on plan; this is an estimate.">
                  <span className="audiobook-youtube-usd"> {approxUsd}</span>
                </Tooltip>
              )}
            </Text>
          </div>
          <Space wrap>
            <Button key="cancel" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              key="generate"
              type="primary"
              onClick={onGenerate}
              disabled={!hasEnoughCredits}
              icon={<YoutubeOutlined />}
            >
              Generate Video ({creditsRequired} credits)
            </Button>
          </Space>
        </div>
      }
      width={900}
    >
      <div className="audiobook-youtube-modal-body">
      <div className="audiobook-youtube-book-summary">
        <Row gutter={[24, 0]} align="middle">
          <Col xs={24} md={12}>
            <Text strong className="audiobook-youtube-book-title">
              <FileTextOutlined style={{ marginRight: 8 }} />
              {bookTitle}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Space>
              <Text type="secondary">Words:</Text>
              <Text strong>{totalWordCount.toLocaleString()}</Text>
            </Space>
          </Col>
          <Col xs={12} md={6}>
            <Space>
              <Text type="secondary">Est. Duration:</Text>
              <Text strong>{Math.floor(estimatedDuration / 60)}h {estimatedDuration % 60}m</Text>
            </Space>
          </Col>
        </Row>
      </div>

      <Row gutter={24} align="top">
        <Col xs={24} md={12}>
          <div className="audiobook-youtube-modal-block">
          <label className="audiobook-youtube-field-label">
            <strong>Video Cover Image:</strong>
          </label>
          
          <Select
            value={useCustomCover ? 'custom' : 'existing'}
            onChange={(value) => onUseCustomCoverChange(value === 'custom')}
            className="audiobook-youtube-control"
          >
            <Select.Option value="existing">Use Book Cover</Select.Option>
            <Select.Option value="custom">Upload Custom Cover</Select.Option>
          </Select>

          {!useCustomCover ? (
            <div className="audiobook-youtube-cover-preview">
              {bookCoverUrl ? (
                <img 
                  src={bookCoverUrl} 
                  alt="Book Cover" 
                  className="audiobook-youtube-cover-image"
                />
              ) : (
                <Text type="secondary">No book cover available</Text>
              )}
            </div>
          ) : (
            <div className="audiobook-youtube-upload-wrap">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onCustomCoverUpload(file);
                }}
                style={{ display: 'none' }}
                id="cover-upload"
              />
              <label htmlFor="cover-upload">
                <div className="audiobook-youtube-cover-preview audiobook-youtube-cover-upload">
                  {customCoverPreview ? (
                    <>
                      <img 
                        src={customCoverPreview} 
                        alt="Custom Cover" 
                        className="audiobook-youtube-custom-cover-image"
                      />
                      <Text type="secondary" className="audiobook-youtube-help-text">Click to change</Text>
                    </>
                  ) : (
                    <>
                      <CloudUploadOutlined className="audiobook-youtube-upload-icon" />
                      <div className="audiobook-youtube-upload-title">
                        <Text>Click to upload cover image</Text>
                      </div>
                      <div className="audiobook-youtube-upload-help">
                        <Text type="secondary">
                          Recommended: 1280x720 or 16:9 aspect ratio
                        </Text>
                      </div>
                    </>
                  )}
                </div>
              </label>
            </div>
          )}
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div className="audiobook-youtube-modal-block">
          <label className="audiobook-youtube-field-label">
            <strong>Video Settings:</strong>
          </label>
          <div className="audiobook-youtube-settings-panel">
            <div className="audiobook-youtube-setting-group">
              <Text type="secondary" className="audiobook-youtube-setting-label">Resolution:</Text>
              <Select
                value={selectedResolution}
                onChange={onResolutionChange}
                className="audiobook-youtube-control"
              >
                <Select.Option value="480p">
                  <Space>
                    <span>480p (854×480)</span>
                    <Tag color="green">Standard</Tag>
                  </Space>
                </Select.Option>
                <Select.Option value="720p">
                  <Space>
                    <span>720p (1280×720)</span>
                    <Tag color="blue">HD</Tag>
                  </Space>
                </Select.Option>
              </Select>
            </div>

            <Row gutter={[24, 12]}>
              <Col span={12}>
                <Space size="small">
                  <Text type="secondary">Format:</Text>
                  <Text>MP4</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space size="small">
                  <Text type="secondary">Audio:</Text>
                  <Text>AAC</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space size="small">
                  <Text type="secondary">Visual:</Text>
                  <Text>Static image</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space size="small">
                  <Text type="secondary">Est. Size:</Text>
                  <Text>
                    ~{selectedResolution === '720p' 
                      ? Math.ceil(estimatedHours * 100) 
                      : Math.ceil(estimatedHours * 80)
                    } MB
                  </Text>
                </Space>
              </Col>
            </Row>
          </div>

          <Alert
            message="Processing Time"
            description={`Estimated generation time: ${generationTimeMinutes} minute${generationTimeMinutes > 1 ? 's' : ''}`}
            type="info"
            showIcon
            className="audiobook-youtube-processing-alert"
          />
          </div>
        </Col>
      </Row>

      <div className="audiobook-youtube-credit-panel">
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic 
              className={`audiobook-credit-stat ${hasEnoughCredits ? 'is-positive' : 'is-danger'}`}
              title="Your Balance" 
              value={creditBalance} 
              suffix="credits"
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic 
              className="audiobook-credit-stat"
              title="Required" 
              value={creditsRequired} 
              suffix="credits"
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic 
              className={`audiobook-credit-stat ${hasEnoughCredits ? 'is-accent' : 'is-danger'}`}
              title="Remaining" 
              value={Math.max(0, creditBalance - creditsRequired)} 
              suffix="credits"
            />
          </Col>
        </Row>
      </div>

      <div className="audiobook-youtube-cost-note">
        <Text type="secondary">
          Cost calculation: {
            fullAudioDurationSeconds != null
              ? `${Math.floor(fullAudioDurationSeconds / 60)} minutes (${(fullAudioDurationSeconds / 3600).toFixed(2)} hours)`
              : `${totalWordCount.toLocaleString()} words ~= ${estimatedHours.toFixed(2)} hours`
          } x {creditsPerHour} credits/hour = {creditsRequired} credits
        </Text>
      </div>

      {!hasEnoughCredits && (
        <Alert
          message="Insufficient Credits"
          description={
            <Space direction="vertical" size={0}>
              <Text>You need {shortfallCredits.toLocaleString()} more credits to generate this video.</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Purchase more credits to continue with video generation.
              </Text>
            </Space>
          }
          type="error"
          showIcon
          className="audiobook-youtube-insufficient-alert"
          action={
            <Button size="small" type="primary" danger onClick={handleTopUpCredits} disabled={!onTopUpCredits}>
              Get Credits
            </Button>
          }
        />
      )}
      </div>
    </Modal>
  );
};

type ChapterStatusFilter = 'all' | 'ready' | 'incomplete' | 'error';
type AudioTone = 'success' | 'warning' | 'danger' | 'muted';

const resolveAudioTone = (status?: string): AudioTone => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'all_audio') return 'success';
  if (normalized === 'partial_audio') return 'warning';
  if (normalized === 'error') return 'danger';
  return 'muted';
};

const resolveStatusTag = (status?: string): { show: boolean; text: string; color: string } => {
  const normalized = (status || '').toLowerCase();
  if (!normalized || normalized === 'no_audio') {
    return { show: true, text: 'No Audio', color: 'default' };
  }
  if (normalized === 'all_audio') {
    return { show: false, text: '', color: 'default' };
  }
  if (normalized === 'partial_audio') {
    return { show: true, text: 'Partial Audio', color: 'warning' };
  }
  if (normalized === 'error') {
    return { show: true, text: 'Error', color: 'error' };
  }
  return { show: true, text: normalized.replace(/_/g, ' '), color: 'default' };
};

type AudiobookHeaderProps = {
  languageTitle: string;
  languageLabel: string;
  hasPendingAudioTranslations: boolean;
  onCreateAudiobook: () => void;
};

const AudiobookHeader: React.FC<AudiobookHeaderProps> = ({
  languageTitle,
  languageLabel,
  hasPendingAudioTranslations,
  onCreateAudiobook,
}) => (
  <section className="audiobook-workflow-section audiobook-header-section" data-testid="tutorial:audiobook:header">
    <div
      data-tour-id="audiobook-overview"
      className="audiobook-header-row"
    >
      <div data-tour-id="audiobook-language" className="audiobook-title-block">
        <Title level={4} style={{ margin: 0 }}>
          <SoundOutlined /> {languageTitle} Audiobook
        </Title>
        <Text type="secondary" className="audiobook-subtitle">
          Language: {languageLabel}
        </Text>
      </div>
      <Space wrap>
        <Tooltip
          title={
            hasPendingAudioTranslations
              ? 'Audiobook generation is in progress. Please wait until it completes.'
              : 'Open audiobook creation options'
          }
        >
          <Button
            data-tour-id="audiobook-create-button"
            data-testid="tutorial:audiobook:create-button"
            type="primary"
            icon={<AudioOutlined />}
            onClick={onCreateAudiobook}
            disabled={hasPendingAudioTranslations}
          >
            Create audiobook
          </Button>
        </Tooltip>
      </Space>
    </div>
  </section>
);

type AudioSegmentCardProps = {
  id: string;
  title: string;
  status: string;
  voice?: string;
  aiModel?: string;
  formats?: { [format: string]: string };
  processedText?: string;
  audioUrl?: string;
  isPlaying: boolean;
  isDeleting?: boolean;
  textExpanded?: boolean;
  showTextToggle?: boolean;
  onToggleText?: () => void;
  onPlayPause?: () => void;
  onDelete?: () => void;
  audioDuration: number;
  audioProgress: number;
  onSeek: (value: number) => void;
  formatTime: (seconds: number) => string;
  filenamePrefix: string;
  bookLanguage: string;
  selectedLanguage: string;
};

const AudioSegmentCard: React.FC<AudioSegmentCardProps> = ({
  id,
  title,
  status,
  voice,
  aiModel,
  formats,
  processedText,
  audioUrl,
  isPlaying,
  isDeleting = false,
  textExpanded = false,
  showTextToggle = false,
  onToggleText,
  onPlayPause,
  onDelete,
  audioDuration,
  audioProgress,
  onSeek,
  formatTime,
  filenamePrefix,
  bookLanguage,
  selectedLanguage,
}) => {
  const tone = resolveAudioTone(status);
  const statusTag = resolveStatusTag(status);
  const isReady = (status || '').toLowerCase() === 'all_audio';

  return (
    <Card size="small" className={`audiobook-segment-card audiobook-tone-${tone}`}>
      <div className="audiobook-segment-header">
        <div>
          <Text strong>{title}</Text>
          <div className="audiobook-segment-tags">
            {statusTag.show && <Tag color={statusTag.color}>{statusTag.text}</Tag>}
            {isReady && voice && (
              <Tag color="blue">
                Voice: {getVoiceDisplayName(voice, undefined, bookLanguage, selectedLanguage)}
                {aiModel && ` - ${aiModel}`}
              </Tag>
            )}
          </div>
        </div>

        <Space>
          {!!audioUrl && !!onPlayPause && (
            <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
              <Button
                type="primary"
                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                size="small"
                onClick={onPlayPause}
                aria-label={`${isPlaying ? 'Pause' : 'Play'} ${title} audio`}
              />
            </Tooltip>
          )}

          {isReady && !!onDelete && (
            <Tooltip title="Delete audio">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={isDeleting}
                onClick={onDelete}
                aria-label={`Delete ${title} audio`}
              />
            </Tooltip>
          )}

          {isReady && showTextToggle && !!onToggleText && (
            <Tooltip title={textExpanded ? 'Hide Text' : 'Show Text'}>
              <Button
                size="small"
                icon={textExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={onToggleText}
                aria-label={`${textExpanded ? 'Hide' : 'Show'} ${title} text`}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      <div className="audiobook-download-tags">
        {formats &&
          Object.entries(formats).map(([format, fileUrl]) => (
            <AudioDownloadTag
              key={`${id}-${format}`}
              fileUrl={fileUrl}
              filename={`${filenamePrefix}-${format.toLowerCase()}.${format.toLowerCase()}`}
              format={format}
            />
          ))}
      </div>

      {textExpanded && !!processedText && (
        <div className="audiobook-segment-text">
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{title} Text</Text>
          <div className="audiobook-segment-text-content">
            <Text>{processedText}</Text>
          </div>
        </div>
      )}

      {isPlaying && (
        <div className="audio-controls audiobook-audio-controls">
          <Slider
            min={0}
            max={audioDuration}
            value={audioProgress}
            onChange={onSeek}
            tooltip={{ formatter: (value) => formatTime(value || 0) }}
          />
          <div className="audiobook-audio-times">
            <Text type="secondary">{formatTime(audioProgress)}</Text>
            <Text type="secondary">{formatTime(audioDuration)}</Text>
          </div>
        </div>
      )}
    </Card>
  );
};

type ChapterAudioCardProps = {
  chapterId: string;
  chapter: ChapterAudioStatus['chapters'][string];
  isPlaying: boolean;
  isDeleting: boolean;
  onPlayPause: () => void;
  onDelete: () => void;
  audioDuration: number;
  audioProgress: number;
  onSeek: (value: number) => void;
  formatTime: (seconds: number) => string;
  bookLanguage: string;
  selectedLanguage: string;
};

const ChapterAudioCard: React.FC<ChapterAudioCardProps> = ({
  chapterId,
  chapter,
  isPlaying,
  isDeleting,
  onPlayPause,
  onDelete,
  audioDuration,
  audioProgress,
  onSeek,
  formatTime,
  bookLanguage,
  selectedLanguage,
}) => {
  const statusTag = resolveStatusTag(chapter.status);

  return (
    <Card
      size="small"
      className={`audiobook-segment-card audiobook-tone-${resolveAudioTone(chapter.status)} audiobook-chapter-card`}
    >
      <div className="audiobook-segment-header">
        <div>
          <Text strong>{chapter.name}</Text>
          <div className="audiobook-segment-tags">
            {statusTag.show && <Tag color={statusTag.color}>{statusTag.text}</Tag>}
            {chapter.status === 'all_audio' && chapter.voice && (
              <Tag color="blue">
                Voice: {getVoiceDisplayName(chapter.voice, undefined, bookLanguage, selectedLanguage)}
                {chapter.aiModel && ` - ${chapter.aiModel}`}
              </Tag>
            )}
          </div>
        </div>

        <Space>
          {!!chapter.combined_audio_url && (
            <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
              <Button
                type="primary"
                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                size="small"
                onClick={onPlayPause}
                aria-label={`${isPlaying ? 'Pause' : 'Play'} ${chapter.name} audio`}
              />
            </Tooltip>
          )}
          {chapter.status === 'all_audio' && (
            <Tooltip title="Delete audio">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={isDeleting}
                onClick={onDelete}
                aria-label={`Delete ${chapter.name} audio`}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      <div className="audiobook-download-tags">
        {chapter.formats &&
          Object.keys(chapter.formats).map((format) => (
            <AudioDownloadTag
              key={`${chapterId}-${format}`}
              fileUrl={chapter.formats![format]}
              filename={`${chapter.name}-${format.toLowerCase()}.${format.toLowerCase()}`}
              format={format}
            />
          ))}
      </div>

      {isPlaying && (
        <div className="audio-controls audiobook-audio-controls">
          <Slider
            min={0}
            max={audioDuration}
            value={audioProgress}
            onChange={onSeek}
            tooltip={{ formatter: (value) => formatTime(value || 0) }}
          />
          <div className="audiobook-audio-times">
            <Text type="secondary">{formatTime(audioProgress)}</Text>
            <Text type="secondary">{formatTime(audioDuration)}</Text>
          </div>
        </div>
      )}
    </Card>
  );
};

type ChapterAudioListProps = {
  chapters: ChapterAudioStatus['chapters'];
  chapterOrderMap: { [key: string]: number };
  showOnlyGenerated: boolean;
  onShowOnlyGeneratedChange: (value: boolean) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilter: ChapterStatusFilter;
  onStatusFilterChange: (value: ChapterStatusFilter) => void;
  renderChapterCard: (chapterId: string, chapter: ChapterAudioStatus['chapters'][string]) => React.ReactNode;
};

const ChapterAudioList: React.FC<ChapterAudioListProps> = ({
  chapters,
  chapterOrderMap,
  showOnlyGenerated,
  onShowOnlyGeneratedChange,
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  renderChapterCard,
}) => {
  const sortedAndFilteredEntries = React.useMemo(() => {
    let entries = Object.entries(chapters).sort((a, b) => {
      const aIndex = chapterOrderMap[a[0]] ?? Infinity;
      const bIndex = chapterOrderMap[b[0]] ?? Infinity;
      return aIndex - bIndex;
    });

    if (showOnlyGenerated) {
      entries = entries.filter(([_, chapter]) =>
        chapter.combined_audio_url || (chapter.formats && Object.keys(chapter.formats).length > 0)
      );
    }

    if (statusFilter !== 'all') {
      entries = entries.filter(([_, chapter]) => {
        const normalized = (chapter.status || '').toLowerCase();
        if (statusFilter === 'ready') return normalized === 'all_audio';
        if (statusFilter === 'error') return normalized === 'error';
        return normalized !== 'all_audio' && normalized !== 'error';
      });
    }

    if (searchValue.trim()) {
      const needle = searchValue.toLowerCase();
      entries = entries.filter(([_, chapter]) => chapter.name.toLowerCase().includes(needle));
    }

    return entries;
  }, [chapters, chapterOrderMap, showOnlyGenerated, statusFilter, searchValue]);

  return (
    <div className="audiobook-chapters-section" data-testid="tutorial:audiobook:chapters">
      <div className="audiobook-section-header">
        <div>
          <Title level={5}>Chapter Audio</Title>
          <Text type="secondary">
            Review generated chapter files, filter by status, and play or download completed audio.
          </Text>
        </div>
      </div>
      <div className="audiobook-filter-row" data-testid="tutorial:audiobook:chapter-filters">
        <Input
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          allowClear
          placeholder="Search chapters"
          prefix={<SearchOutlined />}
          aria-label="Search chapters"
          className="audiobook-filter-search"
        />
        <Select
          value={statusFilter}
          onChange={onStatusFilterChange}
          aria-label="Filter chapters by status"
          className="audiobook-filter-select"
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'ready', label: 'Ready' },
            { value: 'incomplete', label: 'Incomplete' },
            { value: 'error', label: 'Error' },
          ]}
        />
        <Space align="center">
          <FilterOutlined />
          <Switch
            checked={showOnlyGenerated}
            onChange={onShowOnlyGeneratedChange}
            aria-label="Show only chapters with generated audio"
          />
          <Text>Generated only</Text>
        </Space>
        <Text type="secondary" className="audiobook-filter-count">
          {sortedAndFilteredEntries.length} chapter{sortedAndFilteredEntries.length === 1 ? '' : 's'}
        </Text>
      </div>

      {sortedAndFilteredEntries.length === 0 ? (
        <Empty description="No chapters match your filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Row data-tour-id="audiobook-chapter-grid" data-testid="tutorial:audiobook:chapter-grid" gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            {sortedAndFilteredEntries
              .filter((_, index) => index % 2 === 0)
              .map(([chapterId, chapter]) => renderChapterCard(chapterId, chapter))}
          </Col>
          <Col xs={24} lg={12}>
            {sortedAndFilteredEntries
              .filter((_, index) => index % 2 === 1)
              .map(([chapterId, chapter]) => renderChapterCard(chapterId, chapter))}
          </Col>
        </Row>
      )}
    </div>
  );
};

type DistributionSectionProps = {
  chapterAudioStatus: ChapterAudioStatus;
  book: Book;
  sanitizedTitle: string;
  onOpenExportModal: () => void;
  onDeleteSection: (section: 'combined' | 'chapters') => void;
  youtubeVideoUrl: string | null;
  onDeleteYoutubeVideo: () => void;
  isDeletingYoutubeVideo: boolean;
  onOpenYoutubeModal: () => void;
  isGeneratingYoutubeVideo: boolean;
  hasFullMp3: boolean;
  hasPendingAudioTranslations: boolean;
};

const DistributionSection: React.FC<DistributionSectionProps> = ({
  chapterAudioStatus,
  book,
  sanitizedTitle,
  onOpenExportModal,
  onDeleteSection,
  youtubeVideoUrl,
  onDeleteYoutubeVideo,
  isDeletingYoutubeVideo,
  onOpenYoutubeModal,
  isGeneratingYoutubeVideo,
  hasFullMp3,
  hasPendingAudioTranslations,
}) => {
  const fullFormats = chapterAudioStatus.full_book?.formats || {};
  const combinedFormats = Object.entries(fullFormats).filter(([fmt]) => !fmt.startsWith('chapters_zip'));
  const chapterZipFormats = Object.entries(fullFormats).filter(([fmt]) => fmt.startsWith('chapters_zip'));
  const hasCombined = combinedFormats.length > 0 || !!chapterAudioStatus.lpf_file;
  const hasChapterZip = chapterZipFormats.length > 0;
  const youtubeDisabledReason = !hasFullMp3
    ? 'Prepare full MP3 files first to enable YouTube generation.'
    : hasPendingAudioTranslations
      ? 'Wait for current audiobook generation to finish before creating a YouTube version.'
      : null;

  return (
    <div className="audiobook-distribution-section" data-testid="tutorial:audiobook:distribution">
      <div className="audiobook-section-header">
        <div>
          <Title level={5}>Distribution</Title>
          <Text type="secondary">
            Prepare audiobook bundles, manage downloadable files, and create a YouTube-ready video.
          </Text>
        </div>
      </div>
      <section
        data-tour-id="audiobook-full-card"
        data-testid="tutorial:audiobook:download-files"
        className="audiobook-workflow-section audiobook-distribution-card"
      >
        <div className="audiobook-card-section-header">
          <Space>
            <DownloadOutlined />
            <Text strong>Full Audiobook Files</Text>
          </Space>
        </div>
        {!hasCombined && !hasChapterZip ? (
          <div className="audiobook-empty-action">
            <Text style={{ display: 'block', marginBottom: 12 }}>
              No full audiobook files are prepared yet. Generate chapter audio, then prepare downloadable bundles.
            </Text>
            <Button
              type="primary"
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={onOpenExportModal}
              disabled={chapterAudioStatus.summary.all_audio === 0}
              data-testid="tutorial:audiobook:prepare-downloads"
            >
              Prepare Downloadable Audiobook Files
            </Button>
          </div>
        ) : (
          <Row gutter={[16, 16]} className="audiobook-distribution-grid">
            <Col xs={24} md={12}>
              <div className="audiobook-subsection-header">
                <Text strong>Combined Audiobook</Text>
                {hasCombined && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDeleteSection('combined')}
                    aria-label="Delete combined audiobook files"
                  />
                )}
              </div>
              {hasCombined ? (
                <div className="audiobook-download-tags">
                  {combinedFormats.map(([key, meta]) => {
                    const parts = key.split('_');
                    const ext = parts[parts.length - 1];
                    return (
                      <AudioDownloadTag
                        key={key}
                        fileUrl={meta.url}
                        filename={`${book.title}-full.${ext}`}
                        format={ext}
                      />
                    );
                  })}
                  {chapterAudioStatus.lpf_file && (
                    <AudioDownloadTag
                      fileUrl={chapterAudioStatus.lpf_file}
                      filename={`${sanitizedTitle}.lpf`}
                      format="LPF"
                    />
                  )}
                </div>
              ) : (
                <div className="audiobook-empty-action">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CloudUploadOutlined />}
                    onClick={onOpenExportModal}
                    disabled={chapterAudioStatus.summary.all_audio === 0}
                  >
                    Prepare Downloadable Audiobook Files
                  </Button>
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <div className="audiobook-subsection-header">
                <Text strong>Individual Chapter Files (Zipped)</Text>
                {hasChapterZip && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDeleteSection('chapters')}
                    aria-label="Delete chapter zip audiobook files"
                  />
                )}
              </div>
              {hasChapterZip ? (
                <div className="audiobook-download-tags">
                  {chapterZipFormats.map(([fmt, meta]) => {
                    const parts = fmt.split('_');
                    const audioFmt = parts[2] || parts[1];
                    return (
                      <AudioDownloadTag
                        key={fmt}
                        fileUrl={meta.url}
                        filename={`${sanitizedTitle}-chapters-${audioFmt}.zip`}
                        format={audioFmt.toUpperCase()}
                        isChapterZip={true}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="audiobook-empty-action">
                  <Button
                    type="primary"
                    size="small"
                    onClick={onOpenExportModal}
                    disabled={chapterAudioStatus.summary.all_audio === 0}
                  >
                    Prepare Downloadable Audiobook Files
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        )}

        <div className="audiobook-section-separator" />
	        <div data-tour-id="audiobook-youtube" className="audiobook-youtube-inline" data-testid="tutorial:audiobook:youtube">
          <div className="audiobook-subsection-header">
            <Space>
              <YoutubeOutlined />
              <Text strong>YouTube Video</Text>
            </Space>
            {!!youtubeVideoUrl && (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                aria-label="Delete YouTube video"
                data-testid="youtube-delete-button"
                onClick={onDeleteYoutubeVideo}
                loading={isDeletingYoutubeVideo}
              />
            )}
          </div>

          {!!youtubeDisabledReason && !youtubeVideoUrl && (
            <Alert
              type="info"
              showIcon
              message={youtubeDisabledReason}
              className="audiobook-youtube-inline-alert"
            />
          )}

          {youtubeVideoUrl ? (
            <div className="audiobook-download-tags">
              <AudioDownloadTag
                fileUrl={youtubeVideoUrl}
                filename={`${sanitizedTitle}-youtube.mp4`}
                format="MP4"
              />
            </div>
          ) : (
            <div className="audiobook-empty-action">
	              <Button
	                type="primary"
	                size="small"
	                data-testid="youtube-generate-trigger"
                onClick={onOpenYoutubeModal}
                loading={isGeneratingYoutubeVideo}
                disabled={!hasFullMp3 || hasPendingAudioTranslations}
              >
                Generate Video
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const AudiobookTab: React.FC<AudiobookTabProps> = ({ 
  bookId, 
  book, 
  selectedLanguage = 'original',
  audiobookVersionOptions = [],
  onTranslateText,
  auth,
  bookLanguage = 'English',
  onLoadingChange,
  isLangTesterUser,
  chapterWordCounts,
  isBetaUser,
  languageCovers,
  startTourSignal,
  onTopUpCredits,
  testOverrides,
}) => {
  const overrides = testOverrides ?? {};
  const tutorialSandbox = useTutorialSandbox();
  const sandboxFixturePayload = tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture' && tutorialSandbox.fixturePayload
    ? tutorialSandbox.fixturePayload
    : null;
  const sandboxChapterAudioStatus = sandboxFixturePayload?.chapterAudioStatus as ChapterAudioStatus | undefined;
  const sandboxCreditBalance = sandboxFixturePayload?.creditBalance as CreditBalance | undefined;
  const initialChapterStatus = typeof overrides.initialChapterAudioStatus !== 'undefined'
    ? overrides.initialChapterAudioStatus
    : (typeof sandboxChapterAudioStatus !== 'undefined' ? sandboxChapterAudioStatus : null);
  const initialCreditBalance = typeof overrides.initialCreditBalance !== 'undefined'
    ? overrides.initialCreditBalance
    : (typeof sandboxCreditBalance !== 'undefined' ? sandboxCreditBalance : null);
  const skipChapterFetch = !!overrides.skipChapterStatusFetch || typeof sandboxChapterAudioStatus !== 'undefined';
  const skipCreditFetch = !!overrides.skipCreditFetch || typeof sandboxCreditBalance !== 'undefined';
  const youtubeGenerationImpl = overrides.youtubeGenerationImpl ?? runYoutubeVideoGeneration;
  const youtubeDeletionImpl = overrides.youtubeDeletionImpl ?? runYoutubeVideoDeletion;
  const { translations, refreshTranslations, addOptimisticTranslation } = useTranslationStatus();
  const [isAudiobookModalVisible, setIsAudiobookModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(!initialChapterStatus);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string>('Original');
  const [audiobookProcessingError, setAudiobookProcessingError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(initialCreditBalance);
  
  // Audio status state
  const [chapterAudioStatus, setChapterAudioStatus] = useState<ChapterAudioStatus | null>(initialChapterStatus);
  
  // Audio player state
  const [playingChapterId, setPlayingChapterId] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [audioProgress, setAudioProgress] = useState<number>(0);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const audioIntervalRef = useRef<number | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);


  const [currentChapter, setCurrentChapter] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [chapterVoices, setChapterVoices] = useState<{ [chapterId: string]: string }>({});

  const [isExportModalVisible, setIsExportModalVisible] = useState<boolean>(false);

  const [introTextExpanded, setIntroTextExpanded] = useState<boolean>(false);
  const [outroTextExpanded, setOutroTextExpanded] = useState<boolean>(false);
  const sanitizedTitle = book.title.replace(/[<>:"\/\\|?*\s]+/g, '_');
  const [showOnlyGenerated, setShowOnlyGenerated] = useState<boolean>(false);
  const [chapterSearchValue, setChapterSearchValue] = useState<string>('');
  const [chapterStatusFilter, setChapterStatusFilter] = useState<ChapterStatusFilter>('all');
const [sectionToDelete, setSectionToDelete] = useState<'combined' | 'chapters' | null>(null);
const [isDeleteSectionModalVisible, setIsDeleteSectionModalVisible] = useState<boolean>(false);
const [deletingSection, setDeletingSection] = useState<boolean>(false);
const prevPendingCountRef = useRef<number>(0);
const lastExternalTourSignalRef = useRef<number | null>(startTourSignal ?? null);


const [youtubeVideoUrl, setYoutubeVideoUrl] = useState<string | null>(
  initialChapterStatus?.youtube_video?.url ?? null
);

  useEffect(() => {
    if (typeof initialChapterStatus !== 'undefined') {
      setChapterAudioStatus(initialChapterStatus);
      setLoading(!initialChapterStatus);
      setYoutubeVideoUrl(initialChapterStatus?.youtube_video?.url ?? null);
    }
  }, [initialChapterStatus]);

  useEffect(() => {
    if (typeof overrides.initialCreditBalance !== 'undefined') {
      setCreditBalance(overrides.initialCreditBalance);
    }
  }, [overrides.initialCreditBalance]);
const [isDeletingYoutubeVideo, setIsDeletingYoutubeVideo] = useState<boolean>(false);
const [isGeneratingYoutubeVideo, setIsGeneratingYoutubeVideo] = useState<boolean>(false);

  // YouTube video modal states
  const [isYoutubeModalVisible, setIsYoutubeModalVisible] = useState<boolean>(false);
  const [selectedResolution, setSelectedResolution] = useState<'480p' | '720p'>('480p');
  const [useCustomCover, setUseCustomCover] = useState<boolean>(false);
  const [customCoverFile, setCustomCoverFile] = useState<File | null>(null);
  const [customCoverPreview, setCustomCoverPreview] = useState<string | null>(null);



  // Loading states for delete and regenerate actions
  const [actionLoadingStates, setActionLoadingStates] = useState<{
    [key: string]: { deleting: boolean; regenerating: boolean };
  }>({});

  const prevRelevantTranslationsCountRef = useRef<number>(0);

  const userId = auth?.currentUser?.uid ?? null;
  const {
    flags: tourFlags,
    initialized: tourFlagsInitialized,
    markSeen: markAudiobookTourSeen,
  } = useTourFlags(userId, API_BASE);
  const hasSeenAudiobookTour = !!tourFlags.audiobookTabTourSeen;
  const [audiobookTourOpen, setAudiobookTourOpen] = useState(false);
  const [audiobookTourStep, setAudiobookTourStep] = useState(0);
  const [pendingAudiobookTourStep, setPendingAudiobookTourStep] = useState<number | null>(null);
  const [audiobookModalReady, setAudiobookModalReady] = useState(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null, fallbackToBody: boolean = true): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && fallbackToBody && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined'
            ? document.createElement('div')
            : ({} as HTMLElement);
      }
      return tourFallbackRef.current;
    },
    []
  );

  const getPreferredTourTarget = useCallback(
    (ids: string[], fallbackToBody: boolean = true): HTMLElement => {
      for (const id of ids) {
        const el = getTourElement(id);
        if (el) return el;
      }
      return resolveTourTarget(null, fallbackToBody);
    },
    [getTourElement, resolveTourTarget]
  );

  const chapterOrderMap: {[key: string]: number} = book.chapters.reduce<{[key: string]: number}>((map, chapter, index) => {
    map[chapter.chapter_guid] = index;
    return map;
  }, {});

//useEffect((): void => {
//  const fetchElevenlabsVoices = async (): Promise<void> => {
//    try {
//      const response = await axios.get<ElevenlabsVoice[]>(
//        `${API_BASE}/elevenlabsvoices`
//      );
//      console.log('ElevenLabs voices:', response.data);
//    } catch (error) {
//      console.error('Error fetching ElevenLabs voices:', error);
//    }
//  };
//
//  fetchElevenlabsVoices();
//}, []);

  // Initialize loading states when chapter audio status changes
  useEffect(() => {
    if (chapterAudioStatus) {
      const newLoadingStates: {[key: string]: { deleting: boolean; regenerating: boolean }} = {};
      Object.keys(chapterAudioStatus.chapters).forEach(chapterId => {
        newLoadingStates[chapterId] = { deleting: false, regenerating: false };
      });
      // Also add intro and outro
      newLoadingStates['intro'] = { deleting: false, regenerating: false };
      newLoadingStates['outro'] = { deleting: false, regenerating: false };
      
      setActionLoadingStates(newLoadingStates);
	  //console.log("chapterAudioStatus: "+{chapterAudioStatus})
    }
  }, [chapterAudioStatus]);

  const fetchCreditBalance = useCallback(async (userId: string) => {
    try {
      const response = await axios.get(`${API_BASE}/check_credit_balance/`, {
        params: { userID: userId },
      });
      setCreditBalance(response.data);
    } catch (error) {
      console.error('Error fetching credit balance', error);
      message.error('Failed to fetch credit balance', 10);
    }
  }, []);


const handleDeleteSection = (section: 'combined' | 'chapters') => {
  setSectionToDelete(section);
  setIsDeleteSectionModalVisible(true);
};

// Function to confirm and process the deletion
const confirmDeleteSection = async () => {
  if (!sectionToDelete) return;
  
  try {
    setDeletingSection(true);
    
    const response = await axios.delete(
      `${API_BASE}/delete-full-audiobook-section/${bookId}/${sectionToDelete}`,
      {
        params: { version: selectedLanguage },
      }
    );
    
    if (response.data && response.data.success) {
      message.success(`Audiobook ${sectionToDelete === 'combined' ? 'combined' : 'chapter-wise'} files deleted successfully`);
      // Refresh the audio status
      setRefreshTrigger(prev => prev + 1);
    } else {
      message.error(response.data?.error || 'Failed to delete audiobook files');
    }
  } catch (error) {
    console.error('Error deleting audiobook section:', error);
    message.error('Failed to delete audiobook files. Please try again later.');
  } finally {
    setDeletingSection(false);
    setIsDeleteSectionModalVisible(false);
    setSectionToDelete(null);
  }
};

// Determine which cover URL to use based on selected language
const getLanguageSpecificCoverUrl = useCallback(() => {
  // If selectedLanguage is 'original', use the original cover
  if (selectedLanguage === 'original') {
    return languageCovers?.['original'] || book.coverUrl;
  }
  
  // If selectedLanguage is in format 'SourceLang-TargetLang', extract target language
  const selectedTargetLanguage = getSelectedTargetLanguage(selectedLanguage);
  if (selectedTargetLanguage) {
    if (languageCovers?.[selectedTargetLanguage] || languageCovers?.[selectedTargetLanguage.toLowerCase()]) {
      return languageCovers[selectedTargetLanguage] || languageCovers[selectedTargetLanguage.toLowerCase()];
    }
  }
  
  // Check if we have a direct language match
  if (languageCovers?.[selectedLanguage] || languageCovers?.[selectedLanguage.toLowerCase()]) {
    return languageCovers[selectedLanguage] || languageCovers[selectedLanguage.toLowerCase()];
  }
  
  // Fall back to original cover
  return languageCovers?.['original'] || book.coverUrl;
}, [selectedLanguage, languageCovers, book.coverUrl]);

const hasPendingAudioTranslations = React.useMemo(() => {
  return translations.some((t) => {
    // Check basic conditions
    if (t.type !== 'AudioBookGeneration' || 
        t.bookId !== bookId || 
        t.status !== 'pending') {
      return false;
    }
    
    // If original language is selected, check for 'original' or 'Original' version
    if (selectedLanguage === 'original') {
      return t.version?.toLowerCase() === 'original' || !t.version;
    }
    
    // For translated versions, compare the full selectedLanguage with version
    // This handles both "English-Spanish" format and direct language names
    return (
      (t.version !== undefined && t.version.toLowerCase() === selectedLanguage.toLowerCase()) ||
      // Also check if version matches just the target language part
      (Boolean(getSelectedTargetLanguage(selectedLanguage)) && 
       t.version !== undefined && 
       t.version.toLowerCase() === getSelectedTargetLanguage(selectedLanguage).toLowerCase()) ||
      // Check targetLang as fallback
      (t.targetLang !== undefined && t.targetLang.toLowerCase() === selectedLanguage.toLowerCase())
    );
  });
}, [translations, bookId, selectedLanguage]);

//console.log('hasPendingAudioTranslations:', hasPendingAudioTranslations);
//console.log('Selected language:', selectedLanguage);
//console.log('Pending translations:', translations.filter(t => t.type === 'AudioBookGeneration' && t.bookId === bookId && t.status === 'pending' ));

useEffect(() => {
  if (onLoadingChange) {
    onLoadingChange(loading);
  }
}, [loading, onLoadingChange]);

  useEffect(() => {
    if (skipCreditFetch) {
      return;
    }
    if (auth && auth.currentUser) {
      const userId = auth.currentUser.uid; 
      if (userId) {
        fetchCreditBalance(userId);
      }
    }
  }, [auth, fetchCreditBalance, skipCreditFetch]);

const hasFullMp3 = React.useMemo((): boolean => {
  const formats = chapterAudioStatus?.full_book?.formats ?? {};
  return hasFullAudioFormat(formats, 'mp3');
}, [chapterAudioStatus]);


  const getAudiobookChapterWordCount = (chapter: Chapter): number => {
    if (!chapter || !chapter.content) return 0;
    return countWordsIncludingMarkup(chapter.content);
  };

  // Fetch chapter audio status
  const fetchChapterAudioStatus = useCallback(async () => {
    if (!bookId) return;
    
    setLoading(true);
    setError(null); 
    
    try {
      const response = await axios.get(
        `${API_BASE}/check-chapter-audio-status/${bookId}`,
        { params: { version: selectedLanguage } }
      );
      if (response.data && response.data.success) {
        console.log(response.data);
        setChapterAudioStatus(response.data.data);
      } else {
        setError(response.data?.error || 'Failed to load chapter audio status');
      }
    } catch (error) {
      console.error('Error fetching chapter audio status:', error);
      setError('Failed to load chapter audio status. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [bookId, selectedLanguage]);

  // Initial load and refresh when needed
useEffect(() => {
  if (skipChapterFetch) {
    return;
  }
  if (bookId) {
    fetchChapterAudioStatus();
  }
}, [bookId, fetchChapterAudioStatus, refreshTrigger, selectedLanguage, skipChapterFetch]);

  // Clean up audio when component unmounts
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
      }
      if (audioIntervalRef.current) {
        window.clearInterval(audioIntervalRef.current);
      }
    };
  }, []);

useEffect(() => {
  const pendingTranslations = translations.filter(t =>
    t.type === 'AudioBookGeneration' &&
    t.bookId === bookId &&
    t.status === 'pending'
  );
  const pendingCount = pendingTranslations.length;

  // when we go from >0 pending to 0 pending, trigger a refresh
  if (prevPendingCountRef.current > 0 && pendingCount === 0) {
    setRefreshTrigger(count => count + 1);
    // —or— call fetchChapterAudioStatus() directly here
  }

  prevPendingCountRef.current = pendingCount;
}, [translations, bookId]);

const toggleAudiobookModal = () => {
  if (!isAudiobookModalVisible && hasPendingAudioTranslations) {
    message.warning('Audiobook generation is in progress. Please wait.');
    return;
  }

  const nextVisible = !isAudiobookModalVisible;
  setIsAudiobookModalVisible(nextVisible);
  setAudiobookModalReady(false);
  setAudiobookProcessingError(null);
  
  if (nextVisible && auth && auth.currentUser) {
    const userId = auth.currentUser.uid; 
    if (userId) {
      fetchCreditBalance(userId);
    }
  }
};

  const MODAL_STEP_START = 3;
  const MODAL_STEP_END = 7;

  const handleStartAudiobookTour = useCallback(() => {
    if (hasPendingAudioTranslations) {
      message.warning('Finish the current audiobook job before starting the tour.');
      return;
    }
    setAudiobookTourStep(0);
    setPendingAudiobookTourStep(null);
    setAudiobookTourOpen(true);
    setIsAudiobookModalVisible(false);
    setAudiobookModalReady(false);
  }, [hasPendingAudioTranslations]);

  const audiobookTourSteps = React.useMemo<TourProps['steps']>(() => {
    const hasAnyChapterAudio = (chapterAudioStatus?.summary?.all_audio ?? 0) > 0;
    const fullFormats = chapterAudioStatus?.full_book?.formats ?? {};
    const hasCombinedAudio =
      hasFullMp3 ||
      !!chapterAudioStatus?.lpf_file ||
      Object.keys(fullFormats).some((format) => !format.startsWith('chapters_zip'));
    const hasIntroOrOutro =
      Boolean(chapterAudioStatus?.intro?.audio_url || chapterAudioStatus?.intro?.processed_text) ||
      Boolean(chapterAudioStatus?.outro?.audio_url || chapterAudioStatus?.outro?.processed_text);

    const steps: NonNullable<TourProps['steps']> = [
      {
        title: 'Audiobook workspace',
        description: renderTeacherPanel(
          <>
            Manage everything related to narrated editions in one place, from generation to downloads.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-overview']),
        placement: 'bottomLeft',
      },
      {
        title: 'Language context',
        description: renderTeacherPanel(
          <>
            Audiobooks use the active book language or translated edition. Switch languages in the
            book header before creating audio so the output matches the edition you want.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-language', 'audiobook-overview']),
        placement: 'bottomLeft',
      },
      {
        title: 'Create audio',
        description: renderTeacherPanel(
          <>
            Start a new audiobook job whenever you are ready. We will go through that screen next.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-create-button']),
        placement: 'left',
      },
      {
        title: 'Pick your narrator',
        description: renderTeacherPanel(
          <>
            Select the AI model and a voice that fits this project. Options adjust automatically for each language.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-modal-voices']),
        placement: 'bottomLeft',
      },
      {
        title: 'Select chapters',
        description: renderTeacherPanel(
          <>
            Include the chapters you want to generate now. Completed or empty chapters stay out of the queue by default.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-modal-chapters']),
        placement: 'right',
      },
      {
        title: 'Intro & outro credits',
        description: renderTeacherPanel(
          <>
            Personalize the opening and closing credits or disable them entirely. We translate them for you when needed.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-modal-credits']),
        placement: 'topLeft',
      },
      {
        title: 'Credits & total cost',
        description: renderTeacherPanel(
          <>
            Review the credit balance and total audiobook cost before launching. Longer selections
            use more credits, so double-check the word counts.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-modal-cost']),
        placement: 'top',
      },
      {
        title: 'Launch generation',
        description: renderTeacherPanel(
          <>
            When everything looks good, create the audiobook. Progress appears in the status panel and back on this tab.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-modal-submit']),
        placement: 'top',
      },
    ];

    if (hasCombinedAudio) {
      steps.push({
        title: 'Full audiobook files',
        description: renderTeacherPanel(
          <>
            Download combined files or prepare new bundles once chapter audio has finished processing.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-full-card', 'audiobook-overview']),
        placement: 'top',
      });
      steps.push({
        title: 'YouTube editions',
        description: renderTeacherPanel(
          <>
            Turn finished audio into a YouTube-ready video with cover art once full audio is available.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-youtube', 'audiobook-full-card']),
        placement: 'top',
      });
    } else {
      steps.push({
        title: 'After generation',
        description: renderTeacherPanel(
          <>
            Generated chapter files and combined audiobook downloads appear here after processing completes.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-overview']),
        placement: 'top',
      });
    }

    if (hasIntroOrOutro) {
      steps.push({
        title: 'Intro & outro status',
        description: renderTeacherPanel(
          <>
            Review generated credits, play samples, or delete so you can regenerate them if you want a different take.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-intro-outro', 'audiobook-full-card']),
        placement: 'top',
      });
    }

    if (hasAnyChapterAudio) {
      steps.push({
        title: 'Per-chapter controls',
        description: renderTeacherPanel(
          <>
            Each chapter card shows status, voices, and downloads. Use the filters above to focus on finished audio.
          </>
        ),
        target: () => getPreferredTourTarget(['audiobook-chapter-grid', 'audiobook-overview']),
        placement: 'top',
      });
    }

    return steps;
  }, [chapterAudioStatus, getPreferredTourTarget, hasFullMp3, renderTeacherPanel]);

  const handleAudiobookTourChange = useCallback(
    (nextStep: number) => {
      if (nextStep < 0) {
        setAudiobookTourStep(0);
        return;
      }

      const enteringModal = nextStep >= MODAL_STEP_START && nextStep <= MODAL_STEP_END;
      const leavingModal =
        audiobookTourStep >= MODAL_STEP_START &&
        audiobookTourStep <= MODAL_STEP_END &&
        (nextStep < MODAL_STEP_START || nextStep > MODAL_STEP_END);

      if (enteringModal) {
        if (!isAudiobookModalVisible) {
          setIsAudiobookModalVisible(true);
          setAudiobookModalReady(false);
        }
        if (!audiobookModalReady) {
          setPendingAudiobookTourStep(nextStep);
          return;
        }
      } else if (leavingModal && isAudiobookModalVisible) {
        setIsAudiobookModalVisible(false);
        setAudiobookModalReady(false);
      }

      setPendingAudiobookTourStep(null);
      setAudiobookTourStep(nextStep);
    },
    [
      MODAL_STEP_START,
      MODAL_STEP_END,
      audiobookTourStep,
      audiobookModalReady,
      isAudiobookModalVisible,
    ]
  );

  const handleAudiobookTourClose = useCallback(() => {
    setAudiobookTourOpen(false);
    setAudiobookTourStep(0);
    setPendingAudiobookTourStep(null);
    if (isAudiobookModalVisible) {
      setIsAudiobookModalVisible(false);
      setAudiobookModalReady(false);
    }
    void markAudiobookTourSeen('audiobookTabTourSeen');
  }, [isAudiobookModalVisible, markAudiobookTourSeen]);

  const handleAudiobookModalAfterOpenChange = useCallback((open: boolean) => {
    setAudiobookModalReady(open);
  }, []);

  useEffect(() => {
    if (startTourSignal == null) return;
    if (lastExternalTourSignalRef.current === startTourSignal) return;
    lastExternalTourSignalRef.current = startTourSignal;
    if (audiobookTourOpen) return;
    handleStartAudiobookTour();
  }, [startTourSignal, audiobookTourOpen, handleStartAudiobookTour]);

  useEffect(() => {
    if (audiobookTourOpen) return;
    if (!tourFlagsInitialized) return;
    if (hasSeenAudiobookTour) return;
    if (loading) return;
    if (hasPendingAudioTranslations) return;
    setAudiobookTourStep(0);
    setPendingAudiobookTourStep(null);
    setAudiobookTourOpen(true);
    setIsAudiobookModalVisible(false);
    setAudiobookModalReady(false);
  }, [
    audiobookTourOpen,
    tourFlagsInitialized,
    hasSeenAudiobookTour,
    loading,
    hasPendingAudioTranslations,
  ]);

  useEffect(() => {
    if (!audiobookTourOpen) return;
    if (pendingAudiobookTourStep === null) return;

    if (
      pendingAudiobookTourStep >= MODAL_STEP_START &&
      pendingAudiobookTourStep <= MODAL_STEP_END &&
      !audiobookModalReady
    ) {
      return;
    }

    setAudiobookTourStep(pendingAudiobookTourStep);
    setPendingAudiobookTourStep(null);
  }, [
    audiobookTourOpen,
    pendingAudiobookTourStep,
    audiobookModalReady,
    MODAL_STEP_START,
    MODAL_STEP_END,
  ]);

  useEffect(() => {
    if (!audiobookTourOpen) return;
    if (audiobookTourStep < MODAL_STEP_START || audiobookTourStep > MODAL_STEP_END) return;
    if (isAudiobookModalVisible) return;
    if (hasPendingAudioTranslations) return;
    setIsAudiobookModalVisible(true);
    setAudiobookModalReady(false);
  }, [
    audiobookTourOpen,
    audiobookTourStep,
    isAudiobookModalVisible,
    hasPendingAudioTranslations,
    MODAL_STEP_START,
    MODAL_STEP_END,
  ]);

  const handleAudiobookVersionChange = (version: string) => {
    setSelectedVersion(version);
  };

  const handleTranslateAudiobookCreditsText = async (text: string, targetLang: string): Promise<string> => {
    if (!onTranslateText) {
      //message.error('Translation function not available');
      return text;
    }
    
    try {
      return await onTranslateText(text, targetLang);
    } catch (error) {
      console.error('Error translating text:', error);
      //message.error('Failed to translate text');
      return text;
    }
  };

  // Handle playing a chapter audio
  const playChapterAudio = (audioUrl: string, id: string) => {
    // Stop any playing audio first
    stopCurrentAudio();
    
    // If clicking the same chapter, we've already stopped it
    if (playingChapterId === id) {
      setPlayingChapterId(null);
      return;
    }
    
    // Create new audio element
    const audio = new Audio(audioUrl);
    
    // Set up audio event handling
    audio.addEventListener('loadedmetadata', () => {
      setAudioDuration(audio.duration);
    });
    
    audio.addEventListener('ended', () => {
      setPlayingChapterId(null);
      setCurrentAudio(null);
      currentAudioRef.current = null;
      setAudioProgress(0);
      if (audioIntervalRef.current) {
        window.clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
    });
    
    // Play the audio
    audio.play().catch(err => {
      console.error('Error playing audio:', err);
      message.error('Failed to play audio');
      setPlayingChapterId(null);
      setCurrentAudio(null);
      currentAudioRef.current = null;
    });
    
    // Update state
    setPlayingChapterId(id);
    setCurrentAudio(audio);
    currentAudioRef.current = audio;
    
    // Set up interval to update progress
    if (audioIntervalRef.current) {
      window.clearInterval(audioIntervalRef.current);
    }
    
    audioIntervalRef.current = window.setInterval(() => {
      if (audio) {
        setAudioProgress(audio.currentTime);
      }
    }, 1000);
  };

  // Handle stopping current audio
  const stopCurrentAudio = () => {
    const a = currentAudioRef.current || currentAudio;
    if (a) {
      a.pause();
      try { a.currentTime = 0; } catch {}
      setAudioProgress(0);
    }
    
    if (audioIntervalRef.current) {
      window.clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
    }
    
    setPlayingChapterId(null);
    setCurrentAudio(null);
    currentAudioRef.current = null;
  };

  // Stop any playing audio when the selected language changes
  useEffect(() => {
    stopCurrentAudio();
  }, [selectedLanguage]);

  // Also ensure audio is stopped on unmount (tab or view change)
  useEffect(() => {
    return () => {
      const a = currentAudioRef.current;
      if (a) {
        a.pause();
        try { a.currentTime = 0; } catch {}
      }
      if (audioIntervalRef.current) {
        window.clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
      currentAudioRef.current = null;
    };
  }, []);

  // Handle seeking in audio
  const handleSeek = (value: number) => {
    if (currentAudio) {
      currentAudio.currentTime = value;
      setAudioProgress(value);
    }
  };

  // Format time for audio display (MM:SS)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Handler for showing delete chapter modal
  const handleDeleteChapterAudio = (chapterId: string, chapterName: string) => {
    setCurrentChapter({ id: chapterId, name: chapterName });
    setIsDeleteModalVisible(true);
  };

  // Handler for confirming deletion of chapter audio
  const confirmDeleteChapterAudio = async () => {
    try {
      // Set loading state
      setActionLoadingStates(prev => ({
        ...prev,
        [currentChapter.id]: { ...prev[currentChapter.id], deleting: true }
      }));
      
      const response = await axios.delete(
        `${API_BASE}/delete-chapter-audio/${bookId}/${currentChapter.id}`,
        {
          params: { version: selectedLanguage },
        }
      );
      if (response.data && response.data.success) {
        message.success(`Audio for "${currentChapter.name}" deleted successfully`);
        // Refresh the audio status
        setRefreshTrigger(prev => prev + 1);
      } else {
        message.error(response.data?.error || 'Failed to delete audio');
      }
    } catch (error) {
      console.error('Error deleting chapter audio:', error);
      message.error('Failed to delete audio. Please try again later.');
    } finally {
      // Clear loading state
      setActionLoadingStates(prev => ({
        ...prev,
        [currentChapter.id]: { ...prev[currentChapter.id], deleting: false }
      }));
      
      // Close the modal
      setIsDeleteModalVisible(false);
    }
  };



// Helper function to render a chapter card
const renderChapterCard = (chapterId: string, chapter: ChapterAudioStatus['chapters'][string]) => {
  const isDeleting = actionLoadingStates[chapterId]?.deleting || false;

  return (
    <ChapterAudioCard
      key={chapterId}
      chapterId={chapterId}
      chapter={chapter}
      isPlaying={playingChapterId === chapterId}
      isDeleting={isDeleting}
      onPlayPause={() => playChapterAudio(chapter.combined_audio_url!, chapterId)}
      onDelete={() => handleDeleteChapterAudio(chapterId, chapter.name)}
      audioDuration={audioDuration}
      audioProgress={audioProgress}
      onSeek={handleSeek}
      formatTime={formatTime}
      bookLanguage={bookLanguage}
      selectedLanguage={selectedLanguage}
    />
  );
};

const toggleExportModal = () => {
  setIsExportModalVisible(!isExportModalVisible);
};

const handleCreateAudiobook = async ({
  version,
  voice,
  selectedChapters,
  introText,
  outroText,
  aiModel,
}: CreateAudiobookParams): Promise<void> => {
  if (!auth.currentUser) {
    message.error('Not signed in');
    return;
  }

  const userId = auth.currentUser.uid;

  const countWords = (text: string) => countWordsIncludingMarkup(text ?? '');

  // 1) Add _all_ our optimistic chapter‐generation entries immediately:
  selectedChapters.forEach(chapId => {
    const chapter = book.chapters.find(c => c.chapter_guid === chapId);
    if (!chapter) return;
    const tempGuid = `TempAudio-${bookId}-${chapId}-${Date.now()}`;
    addOptimisticTranslation({
      lockGuid: tempGuid,
      chapterGuid: chapId,
      chapterName: chapter.chapter_name,
      sourceLang: version,
      targetLang: version,
      timestamp: new Date().toISOString(),
      status: 'pending',
      bookId,
      userId: auth.currentUser.uid,
      word_count: getAudiobookChapterWordCount(chapter),
      estimatedTimeMinutes: Math.ceil(getAudiobookChapterWordCount(chapter) / 120),
      error: '',
      type: 'AudioBookGeneration',
      version,
      clientIntent: true,
    });
  });

  if (introText) {
    const introWordCount = countWords(introText);
    const tempIntroGuid = `TempAudio-${bookId}-intro-${Date.now()}`;
    addOptimisticTranslation({
      lockGuid: tempIntroGuid,
      chapterGuid: 'intro',
      chapterName: 'Intro',
      sourceLang: version,
      targetLang: version,
      timestamp: new Date().toISOString(),
      status: 'pending',
      bookId,
      userId,
      word_count: introWordCount,
      estimatedTimeMinutes: Math.ceil(introWordCount / 120),
      error: '',
      type: 'AudioBookGeneration',
      version,
      clientIntent: true,
    });
  }
  if (outroText) {
    const outroWordCount = countWords(outroText);
    const tempOutroGuid = `TempAudio-${bookId}-outro-${Date.now()}`;
    addOptimisticTranslation({
      lockGuid: tempOutroGuid,
      chapterGuid: 'outro',
      chapterName: 'Outro',
      sourceLang: version,
      targetLang: version,
      timestamp: new Date().toISOString(),
      status: 'pending',
      bookId,
      userId,
      word_count: outroWordCount,
      estimatedTimeMinutes: Math.ceil(outroWordCount / 120),
      error: '',
      type: 'AudioBookGeneration',
      version,
      clientIntent: true,
    });
  }

  // 2) Close the modal (panels will auto‐open on new pending entries):
  setIsAudiobookModalVisible(false);

  // 3) Make a single API call with all the data
  try {
    await axios.post(`${API_BASE}/generate-audiobook/${bookId}`, {
      userId: auth.currentUser.uid,
      version,
      voice,
      selectedChapters,
      introText,
      outroText,
	  aiModel,
    });
  } catch (error) {
    console.error('Failed to start audiobook generation:', error);
    message.error('Failed to start audiobook generation');
  }
};

const handleExportAudiobook = (options: {
  format: string;
  byChapter: boolean;
  fullAudio: boolean;
}) => {
  if (!auth.currentUser) {
    message.error('Not signed in');
    return;
  }

  const userId = auth.currentUser.uid;
  toggleExportModal();

  const chapterCount =
    chapterAudioStatus?.summary?.total_chapters ?? book.chapters.length;

  // Base fields for optimistic status entries
  const baseStatus: Omit<TranslationStatus, 'chapterName'> = {
    chapterGuid: '',
    sourceLang: bookLanguage,
    targetLang: selectedLanguage,
    timestamp: new Date().toISOString(),
    status: 'pending',
    bookId,
    userId,
    word_count: 0,
    estimatedTimeMinutes: 0,
    lockGuid: 'Temp',
    error: '',
    type: 'AudioBookGeneration',
    version: selectedLanguage,
    clientIntent: true,
  };

  // Prepare one or two status entries depending on user choice
  const statuses: TranslationStatus[] = [];

  if (options.fullAudio) {
    statuses.push({
      ...baseStatus,
      chapterName: `Full Audiobook (${options.format})`,
      estimatedTimeMinutes: Math.max(
        2,
        Math.min(30, 2 + chapterCount * 0.25)
      ),
    });
  }

  if (options.byChapter) {
    statuses.push({
      ...baseStatus,
      chapterName: `By Chapter (${options.format})`,
      estimatedTimeMinutes: Math.max(
        1,
        Math.min(15, 1 + chapterCount * 0.1)
      ),
    });
  }

  // Optimistically add each status entry
  statuses.forEach(status => addOptimisticTranslation(status));

  axios
    .post<{ 
      success: boolean; 
      download_url?: string; 
      error?: string;
      warnings?: string[];
      partial_success?: boolean;
      full_audio_url?: string;
      chapter_zip_url?: string;
    }>(
      `${API_BASE}/export-audiobook/${bookId}`,
      {
        version: selectedLanguage,
        format: options.format,
        by_chapter: options.byChapter,
        full_audio: options.fullAudio,
        user_id: userId,
      }
    )
    .then(response => {
      message.destroy();
      if (response.data.success) {
        if (response.data.partial_success && response.data.warnings) {
          // Show warnings for partial success
          response.data.warnings.forEach(warning => {
            message.warning(warning, 5);
          });
        }
        
        if (response.data.download_url) {
          window.location.href = response.data.download_url;
        } else if (response.data.full_audio_url || response.data.chapter_zip_url) {
          message.success('Export completed. Files are being prepared.');
        }
      } else {
        // Show the actual error message from the server
        //message.error(response.data.error || 'Failed to prepare export');
      }
    })
    .catch(err => {
      message.destroy();
      console.error('Error exporting audiobook:', err);
      message.error('Failed to export audiobook. Please try again later.');
    });
};

// Update the downloadFile function to handle undefined values
const downloadFile = async (
  fileUrl: string | undefined,
  filename: string
): Promise<void> => {
  // guard against undefined URL
  if (!fileUrl) {
    //message.error('No audio URL available for download.');
    return;
  }

  try {
    const response = await axios.get(`${API_BASE}/download-audiofile/`, {
      params: { file_url: fileUrl, filename },
      responseType: 'blob',
    });

    if (response.data.type?.includes('application/json')) {
      const direct = JSON.parse(await response.data.text());
      if (direct.direct_download && direct.download_url) {
        window.location.assign(direct.download_url);
        return;
      }
      throw new Error('Download was not available');
    }
    const blob = new Blob([response.data], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${filename}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    console.error('Download failed', err);
    //message.error('Failed to download file. Please try again.');
  }
};

// Calculate total word count for the book
const getTotalWordCount = useCallback(() => {
  let total = 0;
  
  // Add chapter word counts
  book.chapters.forEach(chapter => {
    total += getAudiobookChapterWordCount(chapter);
  });
  
  // Add intro/outro word counts if they exist
  if (chapterAudioStatus?.intro?.processed_text) {
    total += chapterAudioStatus.intro.processed_text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }
  if (chapterAudioStatus?.outro?.processed_text) {
    total += chapterAudioStatus.outro.processed_text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }
  
  return total;
}, [book, chapterAudioStatus]);



// Handle YouTube video generation
const handleGenerateYoutubeVideo = async () => {
  await youtubeGenerationImpl({
    authUser: auth.currentUser,
    bookId,
    selectedLanguage,
    selectedResolution,
    useCustomCover,
    customCoverFile,
    getTotalWordCount,
    creditBalance,
    axiosInstance: axios,
    messageApi: message,
    addOptimisticTranslation,
    setYoutubeVideoUrl,
    setIsGeneratingYoutubeVideo,
    setIsYoutubeModalVisible,
  });
};

// Handle YouTube video deletion
const refreshYoutubeStatus = useCallback(() => {
  setRefreshTrigger(prev => prev + 1);
}, []);

const handleDeleteYoutubeVideo = async () => {
  Modal.confirm({
    title: 'Delete YouTube Video',
    content: 'Are you sure you want to delete the YouTube video? This action cannot be undone.',
    okText: 'Delete',
    okType: 'danger',
    onOk: () =>
      youtubeDeletionImpl({
        axiosInstance: axios,
        messageApi: message,
        bookId,
        selectedLanguage,
        refreshYoutubeStatus,
        setYoutubeVideoUrl,
        setIsDeletingYoutubeVideo,
      }),
  });
};

// Update the effect to extract YouTube video URL from chapterAudioStatus
useEffect(() => {
  if (chapterAudioStatus?.youtube_video) {
    setYoutubeVideoUrl(chapterAudioStatus.youtube_video.url || null);
  } else {
    setYoutubeVideoUrl(null);
  }
}, [chapterAudioStatus]);

const handleResolutionChange = useCallback((value: '480p' | '720p') => {
    setSelectedResolution(value);
  }, []);

  const handleUseCustomCoverChange = useCallback((value: boolean) => {
    setUseCustomCover(value);
  }, []);

  const handleCustomCoverUpload = useCallback((file: File) => {
    if (file && file.type.startsWith('image/')) {
      setCustomCoverFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomCoverPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      message.error('Please upload a valid image file');
    }
  }, []);

  const handleCancelYoutubeModal = useCallback(() => {
    setIsYoutubeModalVisible(false);
    // Reset states when closing
    setUseCustomCover(false);
    setCustomCoverFile(null);
    setCustomCoverPreview(null);
    setSelectedResolution('480p');
  }, []);

  const languageTitle =
    selectedVersion === 'original' || selectedVersion === 'original (proofread)'
      ? bookLanguage
      : getSelectedTargetLanguage(selectedLanguage) || bookLanguage;
  const languageLabel =
    selectedLanguage === 'original' ? bookLanguage : getSelectedTargetLanguage(selectedLanguage) || selectedLanguage;
  const fullFormatsForSummary = chapterAudioStatus?.full_book?.formats || {};
  const hasCombinedFiles =
    Object.keys(fullFormatsForSummary).some((fmt) => !fmt.startsWith('chapters_zip')) ||
    !!chapterAudioStatus?.lpf_file;
  const hasChapterZipFiles = Object.keys(fullFormatsForSummary).some((fmt) => fmt.startsWith('chapters_zip'));
  const hasIntroAudio = (chapterAudioStatus?.intro?.status || '').toLowerCase() === 'all_audio';
  const hasOutroAudio = (chapterAudioStatus?.outro?.status || '').toLowerCase() === 'all_audio';

  useEffect(() => {
    setIntroTextExpanded(false);
    setOutroTextExpanded(false);
  }, [selectedLanguage, chapterAudioStatus?.intro?.processed_text, chapterAudioStatus?.outro?.processed_text]);

return (
  <div className="audiobook-tab-shell book-detail-tab-shell" data-testid="tutorial:audiobook:workspace">
    <div className="audiobook-tab-content">
      <AudiobookHeader
        languageTitle={languageTitle}
        languageLabel={languageLabel}
        hasPendingAudioTranslations={hasPendingAudioTranslations}
        onCreateAudiobook={toggleAudiobookModal}
      />

      {hasPendingAudioTranslations && (
        <Alert
          type="warning"
          showIcon
          message="Audiobook generation is in progress for this language."
          description="You can still review existing files and downloads while generation completes."
          className="audiobook-inline-alert"
        />
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not load audiobook data"
          description={error}
          className="audiobook-inline-alert"
        />
      )}

      {loading ? (
        <div className="loading-card-wrapper audiobook-loading-state">
          <LoadingCard
            label="Loading audio status..."
            indicator={<FeatherSpinner style={{ width: 32, height: 32 }} />}
          />
        </div>
      ) : !chapterAudioStatus ? (
        <Empty description="No audio status available for this book" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <section className="audiobook-overview-band" aria-label="Audiobook readiness overview" data-testid="tutorial:audiobook:overview-metrics">
            <div className="audiobook-metric-row">
              <span className="audiobook-metric-label">Chapters Ready</span>
              <strong>{chapterAudioStatus.summary.all_audio} / {chapterAudioStatus.summary.total_chapters}</strong>
              <span className="audiobook-metric-note">Chapter audio files available</span>
            </div>
            <div className="audiobook-metric-row">
              <span className="audiobook-metric-label">Intro / Outro</span>
              <strong>{hasIntroAudio ? 'Ready' : 'Missing'} / {hasOutroAudio ? 'Ready' : 'Missing'}</strong>
              <span className="audiobook-metric-note">Opening and closing credits</span>
            </div>
            <div className="audiobook-metric-row">
              <span className="audiobook-metric-label">Full Files</span>
              <strong>{hasCombinedFiles || hasChapterZipFiles ? 'Ready' : 'Not Prepared'}</strong>
              <span className="audiobook-metric-note">Downloadable bundles</span>
            </div>
            <div className="audiobook-metric-row">
              <span className="audiobook-metric-label">YouTube</span>
              <strong>{youtubeVideoUrl ? 'Ready' : 'Not Generated'}</strong>
              <span className="audiobook-metric-note">Video export status</span>
            </div>
          </section>

          <DistributionSection
            chapterAudioStatus={chapterAudioStatus}
            book={book}
            sanitizedTitle={sanitizedTitle}
            onOpenExportModal={toggleExportModal}
            onDeleteSection={handleDeleteSection}
            youtubeVideoUrl={youtubeVideoUrl}
            onDeleteYoutubeVideo={handleDeleteYoutubeVideo}
            isDeletingYoutubeVideo={isDeletingYoutubeVideo}
            onOpenYoutubeModal={() => setIsYoutubeModalVisible(true)}
            isGeneratingYoutubeVideo={isGeneratingYoutubeVideo}
            hasFullMp3={hasFullMp3}
            hasPendingAudioTranslations={hasPendingAudioTranslations}
          />

          <section className="audiobook-workflow-section" data-tour-id="audiobook-intro-outro" data-testid="tutorial:audiobook:intro-outro">
            <div className="audiobook-section-header">
              <div>
                <Title level={5}>Intro and Outro Credits</Title>
                <Text type="secondary">
                  Check opening and closing audio, review processed text, and delete stale files when needed.
                </Text>
              </div>
            </div>
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
              <Col xs={24} md={12}>
                <AudioSegmentCard
                  id="intro"
                  title="Intro"
                  status={chapterAudioStatus.intro.status}
                  voice={chapterAudioStatus.intro.voice}
                  aiModel={chapterAudioStatus.intro.aiModel}
                  formats={chapterAudioStatus.intro.formats}
                  processedText={chapterAudioStatus.intro.processed_text}
                  audioUrl={chapterAudioStatus.intro.audio_url}
                  isPlaying={playingChapterId === 'intro'}
                  isDeleting={actionLoadingStates['intro']?.deleting}
                  textExpanded={introTextExpanded}
                  showTextToggle
                  onToggleText={() => setIntroTextExpanded((prev) => !prev)}
                  onPlayPause={() => playChapterAudio(chapterAudioStatus.intro.audio_url!, 'intro')}
                  onDelete={() => handleDeleteChapterAudio('intro', 'Intro')}
                  audioDuration={audioDuration}
                  audioProgress={audioProgress}
                  onSeek={handleSeek}
                  formatTime={formatTime}
                  filenamePrefix="intro"
                  bookLanguage={bookLanguage}
                  selectedLanguage={selectedLanguage}
                />
              </Col>
              <Col xs={24} md={12}>
                <AudioSegmentCard
                  id="outro"
                  title="Outro"
                  status={chapterAudioStatus.outro.status}
                  voice={chapterAudioStatus.outro.voice}
                  aiModel={chapterAudioStatus.outro.aiModel}
                  formats={chapterAudioStatus.outro.formats}
                  processedText={chapterAudioStatus.outro.processed_text}
                  audioUrl={chapterAudioStatus.outro.audio_url}
                  isPlaying={playingChapterId === 'outro'}
                  isDeleting={actionLoadingStates['outro']?.deleting}
                  textExpanded={outroTextExpanded}
                  showTextToggle
                  onToggleText={() => setOutroTextExpanded((prev) => !prev)}
                  onPlayPause={() => playChapterAudio(chapterAudioStatus.outro.audio_url!, 'outro')}
                  onDelete={() => handleDeleteChapterAudio('outro', 'Outro')}
                  audioDuration={audioDuration}
                  audioProgress={audioProgress}
                  onSeek={handleSeek}
                  formatTime={formatTime}
                  filenamePrefix="outro"
                  bookLanguage={bookLanguage}
                  selectedLanguage={selectedLanguage}
                />
              </Col>
            </Row>
          </section>

          <ChapterAudioList
            chapters={chapterAudioStatus.chapters}
            chapterOrderMap={chapterOrderMap}
            showOnlyGenerated={showOnlyGenerated}
            onShowOnlyGeneratedChange={setShowOnlyGenerated}
            searchValue={chapterSearchValue}
            onSearchChange={setChapterSearchValue}
            statusFilter={chapterStatusFilter}
            onStatusFilterChange={setChapterStatusFilter}
            renderChapterCard={renderChapterCard}
          />
        </>
      )}

      <Modal
        title="Delete Chapter Audio"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        onOk={confirmDeleteChapterAudio}
        okText={actionLoadingStates[currentChapter.id]?.deleting ? 'Deleting...' : 'Delete'}
        okButtonProps={{ danger: true, disabled: actionLoadingStates[currentChapter.id]?.deleting }}
        cancelButtonProps={{ disabled: actionLoadingStates[currentChapter.id]?.deleting }}
      >
        {actionLoadingStates[currentChapter.id]?.deleting ? (
          <div className="loading-card-wrapper" style={{ padding: '30px 0' }}>
            <LoadingCard
              label="Deleting audio..."
              indicator={<FeatherSpinner style={{ width: 32, height: 32 }} />}
            />
          </div>
        ) : (
          <>
            <p>Are you sure you want to delete the audio for chapter "{currentChapter.name}"?</p>
            <p>This action cannot be undone.</p>
          </>
        )}
      </Modal>
      
     
      {/* Audiobook Creation Modal */}
      <AudiobookCreationModal
        isVisible={isAudiobookModalVisible}
        onCancel={toggleAudiobookModal}
        onAfterOpenChange={handleAudiobookModalAfterOpenChange}
        onCreateAudiobook={handleCreateAudiobook}
        creditBalance={(creditBalance?.available_balance ?? creditBalance?.credit_balance) ?? 0}
        planId={creditBalance?.planID}
        chapters={book.chapters.map((ch) => ({
          id: ch.chapter_guid,
          title: ch.chapter_name,
          included: true,
          wordCount: getAudiobookChapterWordCount(ch) 
        }))}
        versionOptions={audiobookVersionOptions}
        onVersionChange={handleAudiobookVersionChange}
        errorMessages={audiobookProcessingError ? [audiobookProcessingError] : []}
        warningMessages={[]}
        book={{ title: book.title, author: book.author, bookId: bookId }}
        onTranslateText={handleTranslateAudiobookCreditsText}    
        version={selectedLanguage}
        bookLanguage={bookLanguage}
        chapterWordCounts={chapterWordCounts}
        initialChapterAudioStatus={chapterAudioStatus}
        skipChapterStatusFetch={skipChapterFetch}
      />
	  
<ExportAudiobookModal
  isVisible={isExportModalVisible}
  onCancel={toggleExportModal}
  onDownload={handleExportAudiobook}
  bookLanguage={selectedLanguage === 'original' ? bookLanguage : getSelectedTargetLanguage(selectedLanguage) || selectedLanguage}
  hasCompletedChapters={(chapterAudioStatus?.summary?.all_audio ?? 0) > 0}
  chapterAudioStatus={chapterAudioStatus}
/>

<Modal
  title={`Delete ${sectionToDelete === 'combined' ? 'Combined' : 'Chapter-wise'} Audiobook Files`}
  open={isDeleteSectionModalVisible}
  onCancel={() => setIsDeleteSectionModalVisible(false)}
  onOk={confirmDeleteSection}
  okText={deletingSection ? 'Deleting...' : 'Delete'}
  okButtonProps={{ danger: true, disabled: deletingSection }}
  cancelButtonProps={{ disabled: deletingSection }}
>
  {deletingSection ? (
    <div className="loading-card-wrapper" style={{ padding: '30px 0' }}>
      <LoadingCard
        label="Deleting audiobook files..."
        indicator={<FeatherSpinner style={{ width: 32, height: 32 }} />}
      />
    </div>
  ) : (
    <>
      <p>Are you sure you want to delete all {sectionToDelete === 'combined' ? 'combined' : 'chapter-wise'} audiobook files?</p>
      <p>This action cannot be undone.</p>
    </>
  )}
</Modal>

      <YoutubeVideoGenerationModal
        isVisible={isYoutubeModalVisible}
        onCancel={handleCancelYoutubeModal}
        onGenerate={handleGenerateYoutubeVideo}
        creditBalance={creditBalance?.credit_balance ?? 0}
        totalWordCount={getTotalWordCount()}
		fullAudioDurationSeconds={chapterAudioStatus?.full_book?.full_audio_duration_seconds}
        bookTitle={book.title}
        bookCoverUrl={getLanguageSpecificCoverUrl()}
        selectedResolution={selectedResolution}
        onResolutionChange={handleResolutionChange}
        useCustomCover={useCustomCover}
        onUseCustomCoverChange={handleUseCustomCoverChange}
        customCoverPreview={customCoverPreview}
        onCustomCoverUpload={handleCustomCoverUpload}
        planId={creditBalance?.planID}
        onTopUpCredits={onTopUpCredits}
      />

      <AppTour
        open={audiobookTourOpen}
        steps={audiobookTourSteps}
        current={audiobookTourStep}
        onChange={handleAudiobookTourChange}
        onClose={handleAudiobookTourClose}
        onFinish={handleAudiobookTourClose}
        zIndex={2100}
      />
    </div>
  </div>
);
};
export default AudiobookTab;
